import { AnimatePresence, motion } from 'framer-motion'
import { X } from 'lucide-react'
import { NavLink } from 'react-router-dom'
import { mobileNavItems, navItems } from '../lib/nav'
import { useAuth } from '../context/AuthContext'
import { useUI } from '../context/UIContext'
import { cn } from '../lib/cn'

/*
 * The bar had its own list of four, carried over from the design repo, and it
 * disagreed with `lib/nav.ts` about three of them — two pointed at screens
 * this app does not have. A second list is how that happens, so there is one
 * list now and `mobileNavItems` is the slice of it that fits across a phone.
 */

export function MobileBottomNav() {
  return (
    <nav className="fixed inset-x-0 bottom-0 z-40 flex items-center justify-evenly gap-2 border-t border-shell-border bg-shell/95 px-4 pb-[max(env(safe-area-inset-bottom),8px)] pt-1.5 backdrop-blur-sm lg:hidden">
      {mobileNavItems.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          className={({ isActive }) =>
            cn(
              'flex w-14 flex-col items-center gap-1 rounded-xl py-2 text-[11px] font-medium transition-colors',
              isActive ? 'text-brand-600' : 'text-shell-text',
            )
          }
        >
          {({ isActive }) => (
            <>
              <item.icon size={20} strokeWidth={isActive ? 2.4 : 2} />
              {item.short}
            </>
          )}
        </NavLink>
      ))}
    </nav>
  )
}

export function MobileNavDrawer() {
  const { mobileNavOpen, setMobileNavOpen } = useUI()
  const { hasRole } = useAuth()
  // The design guarded an `/admin` screen that does not exist here, which
  // meant every item passed and the drawer offered the month roll-up to
  // people the server answers with a 403. The list says which ones are the
  // partner's; this asks it.
  const visibleNavItems = navItems.filter(
    (item) => item.partnerOnly !== true || hasRole('PARTNER')
  )
  return (
    <AnimatePresence>
      {mobileNavOpen && (
        <div className="fixed inset-0 z-[150] lg:hidden">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-ink-950/50"
            onClick={() => setMobileNavOpen(false)}
          />
          <motion.div
            initial={{ x: '-100%' }}
            animate={{ x: 0 }}
            exit={{ x: '-100%' }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            // Solid, like the sidebar it stands in for: the glass panel let the page
            // show through behind the links, and at 288px it covered most of a
            // phone. 240px leaves the page visible enough to tap away from.
            className="relative z-10 flex h-full w-60 max-w-[70vw] flex-col border-r border-shell-border bg-shell shadow-xl"
          >
            <div className="flex items-center justify-between px-3 py-3">
              <div className="flex items-center gap-2.5">
                {/* The logo's wordmark is solid black, so it always needs a
                 * light chip behind it regardless of theme. */}
                <div className="flex items-center justify-center rounded-lg bg-white px-2.5 py-2">
                  <img src="/studiohappens-logo.svg" alt="Studiohappens" className="h-9 w-auto object-contain" />
                </div>
              </div>
              <button
                aria-label="Close menu"
                onClick={() => setMobileNavOpen(false)}
                className="rounded-lg p-1.5 text-shell-text hover:bg-shell-border/50 hover:text-shell-text-active"
              >
                <X size={18} />
              </button>
            </div>
            <nav className="flex-1 space-y-1 overflow-y-auto px-3 pb-4">
              {visibleNavItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={() => setMobileNavOpen(false)}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors',
                      isActive ? 'bg-brand-50 text-brand-600' : 'text-shell-text hover:bg-shell-border/50 hover:text-shell-text-active',
                    )
                  }
                >
                  <item.icon size={18} />
                  {item.label}
                </NavLink>
              ))}
            </nav>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  )
}
